import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trash2, Plus, Minus, Lock, ShoppingBag, ArrowLeft, Tag, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { createCheckout, validateDiscount } from "@/lib/api";

export default function Cart() {
  const { t, lang } = useLanguage();
  const { items, removeItem, updateQty, subtotal } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Discount code state
  const [discountInput, setDiscountInput] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null); // {code, discount_percent}
  const [discountError, setDiscountError] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);

  const discountAmount = appliedDiscount
    ? Math.round(subtotal * appliedDiscount.discount_percent) / 100
    : 0;
  const totalAfterDiscount = Math.max(0, subtotal - discountAmount);

  const applyDiscount = async () => {
    if (!discountInput.trim()) return;
    setDiscountError("");
    setDiscountLoading(true);
    try {
      const res = await validateDiscount(discountInput.trim());
      setAppliedDiscount(res);
      setDiscountInput("");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setDiscountError(
        msg ||
          (lang === "en" ? "Invalid or expired code." : "Ugyldig eller utløpt kode.")
      );
      setAppliedDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountError("");
  };

  // Auto-apply discount code from URL ?code=LANSERING25 or localStorage
  useEffect(() => {
    const codeFromUrl = searchParams.get("code");
    const codeToTry = codeFromUrl || localStorage.getItem("pendingDiscountCode");
    if (codeFromUrl) {
      localStorage.setItem("pendingDiscountCode", codeFromUrl);
    }
    if (codeToTry && !appliedDiscount && items.length > 0) {
      validateDiscount(codeToTry)
        .then((res) => setAppliedDiscount(res))
        .catch(() => localStorage.removeItem("pendingDiscountCode"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, items.length]);

  const checkout = async (e) => {
    e.preventDefault();
    if (!email || items.length === 0) return;
    if (!consent) {
      setError(
        "Du må godta handelsvilkårene for å fullføre kjøpet."
      );
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await createCheckout({
        items: items.map((i) => ({
          product_id: i.id,
          quantity: i.quantity,
          kind: i.kind || "product",
        })),
        customer_email: email,
        customer_name: name,
        origin_url: window.location.origin,
        language: lang,
        discount_code: appliedDiscount ? appliedDiscount.code : null,
      });
      window.location.href = res.url;
    } catch (err) {
      setError(
        lang === "en"
          ? "Could not start checkout. Please try again."
          : "Kunne ikke starte kassen. Prøv igjen."
      );
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 md:py-32 text-center">
        <ShoppingBag
          className="w-12 h-12 text-[#8BA896] mx-auto mb-6"
          strokeWidth={1.2}
        />
        <h1 className="font-heading text-3xl md:text-4xl text-[#2C302E] font-light">
          {t("cart_empty")}
        </h1>
        <Link
          to="/butikk"
          data-testid="empty-cart-shop"
          className="mt-8 inline-block bg-[#2C302E] hover:bg-[#1f221f] text-white rounded-full px-8 py-3 font-medium"
        >
          {t("cart_continue")}
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#F9F8F6] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-[#6B726C] hover:text-[#2C302E] mb-8"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {lang === "en" ? "Continue shopping" : "Fortsett å handle"}
        </button>

        <h1 className="font-heading text-3xl md:text-5xl tracking-tight font-light text-[#2C302E] mb-12">
          {t("cart_title")}
        </h1>

        <div className="grid lg:grid-cols-[1fr_400px] gap-10">
          {/* Items */}
          <div className="space-y-4">
            {items.map((item) => {
              const title = lang === "en" ? item.title_en : item.title_no;
              const isBundle = item.kind === "bundle";
              const detailPath = isBundle ? `/pakke/${item.slug}` : `/produkt/${item.slug}`;
              return (
                <div
                  key={`${item.kind || "product"}-${item.id}`}
                  data-testid={`cart-item-${item.slug}`}
                  className="bg-white border border-[#E5E2DC] rounded-2xl p-4 md:p-5 flex gap-4 items-center"
                >
                  <Link
                    to={detailPath}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-[#F9F8F6] flex-shrink-0 relative"
                  >
                    <img
                      src={item.image_url}
                      alt={title}
                      className="w-full h-full object-cover"
                    />
                    {isBundle && (
                      <span className="absolute top-1 left-1 bg-[#D97B66] text-white text-[9px] font-medium rounded-full px-1.5 py-0.5">
                        BUNDLE
                      </span>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={detailPath}
                      className="font-heading text-base md:text-lg text-[#2C302E] font-medium hover:text-[#D97B66] line-clamp-2"
                    >
                      {title}
                    </Link>
                    <p className="text-sm text-[#6B726C] mt-1">
                      {item.price_nok} NOK
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex items-center border border-[#E5E2DC] rounded-full">
                        <button
                          data-testid={`qty-minus-${item.slug}`}
                          onClick={() => updateQty(item.id, item.quantity - 1, item.kind || "product")}
                          className="p-1.5 hover:bg-[#F9F8F6] rounded-full"
                        >
                          <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                        <span className="px-3 text-sm font-medium min-w-[1.5rem] text-center">
                          {item.quantity}
                        </span>
                        <button
                          data-testid={`qty-plus-${item.slug}`}
                          onClick={() => updateQty(item.id, item.quantity + 1, item.kind || "product")}
                          className="p-1.5 hover:bg-[#F9F8F6] rounded-full"
                        >
                          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                      <button
                        data-testid={`remove-${item.slug}`}
                        onClick={() => removeItem(item.id, item.kind || "product")}
                        className="text-[#6B726C] hover:text-[#D97B66] p-1.5"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <p className="font-heading text-lg text-[#2C302E] font-medium hidden sm:block">
                    {(item.price_nok * item.quantity).toFixed(0)} NOK
                  </p>
                </div>
              );
            })}
          </div>

          {/* Checkout panel */}
          <aside className="bg-white border border-[#E5E2DC] rounded-2xl p-6 md:p-8 h-fit lg:sticky lg:top-28">
            <h2 className="font-heading text-xl text-[#2C302E] font-medium mb-6">
              {t("checkout_title")}
            </h2>

            <form onSubmit={checkout} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
                  {t("checkout_email")} *
                </label>
                <input
                  type="email"
                  required
                  data-testid="checkout-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="navn@eksempel.no"
                  className="mt-2 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-4 py-3 text-[#2C302E] placeholder:text-[#6B726C]/50 focus:outline-none focus:border-[#8BA896]"
                />
                <p className="mt-1.5 text-xs text-[#6B726C]">
                  {t("checkout_email_help")}
                </p>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
                  {t("checkout_name")}
                </label>
                <input
                  type="text"
                  data-testid="checkout-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-4 py-3 text-[#2C302E] focus:outline-none focus:border-[#8BA896]"
                />
              </div>

              <div className="pt-4 border-t border-[#E5E2DC] space-y-3">
                <div className="flex justify-between text-sm text-[#6B726C]">
                  <span>{t("cart_subtotal")}</span>
                  <span data-testid="cart-subtotal">{subtotal.toFixed(0)} NOK</span>
                </div>

                {/* Discount code section */}
                {!appliedDiscount ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B726C]" strokeWidth={1.5} />
                        <input
                          type="text"
                          data-testid="discount-code-input"
                          value={discountInput}
                          onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                          placeholder={lang === "en" ? "Discount code" : "Rabattkode"}
                          className="w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg pl-9 pr-3 py-2 text-sm text-[#2C302E] uppercase tracking-wider placeholder:text-[#6B726C]/50 placeholder:normal-case placeholder:tracking-normal focus:outline-none focus:border-[#8BA896]"
                        />
                      </div>
                      <button
                        type="button"
                        data-testid="discount-apply-btn"
                        onClick={applyDiscount}
                        disabled={discountLoading || !discountInput.trim()}
                        className="px-4 py-2 rounded-lg bg-[#2C302E] hover:bg-[#1f221f] disabled:opacity-50 text-white text-sm font-medium"
                      >
                        {discountLoading
                          ? "..."
                          : lang === "en"
                          ? "Apply"
                          : "Bruk"}
                      </button>
                    </div>
                    {discountError && (
                      <p className="text-xs text-[#D97B66]" data-testid="discount-error">
                        {discountError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div
                    data-testid="discount-applied"
                    className="flex items-center justify-between bg-[#EFF5EE] border border-[#8BA896]/40 rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Tag className="w-3.5 h-3.5 text-[#8BA896]" strokeWidth={2} />
                      <span className="text-sm font-medium text-[#2C302E] uppercase tracking-wider">
                        {appliedDiscount.code}
                      </span>
                      <span className="text-xs text-[#6B726C]">
                        −{appliedDiscount.discount_percent}%
                      </span>
                    </div>
                    <button
                      type="button"
                      data-testid="discount-remove-btn"
                      onClick={removeDiscount}
                      className="text-[#6B726C] hover:text-[#D97B66] p-1"
                      aria-label="Fjern rabattkode"
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                )}

                {appliedDiscount && (
                  <div className="flex justify-between text-sm text-[#8BA896]">
                    <span>
                      {lang === "en" ? "Discount" : "Rabatt"} (
                      {appliedDiscount.discount_percent}%)
                    </span>
                    <span data-testid="cart-discount-amount">
                      −{discountAmount.toFixed(0)} NOK
                    </span>
                  </div>
                )}

                <div className="flex justify-between font-heading text-xl text-[#2C302E] font-medium pt-1 border-t border-[#E5E2DC]">
                  <span>{t("cart_total")}</span>
                  <span data-testid="cart-total">
                    {totalAfterDiscount.toFixed(0)} NOK
                  </span>
                </div>
              </div>

              {error && (
                <p className="text-sm text-[#D97B66]" data-testid="checkout-error">
                  {error}
                </p>
              )}

              {/* Angrerett samtykke - Required by Norwegian law */}
              <label className="flex items-start gap-3 cursor-pointer text-xs text-[#4A4F4A] leading-relaxed bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl p-3">
                <input
                  type="checkbox"
                  required
                  data-testid="checkout-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-[#6B726C] text-[#D97B66] focus:ring-[#D97B66] flex-shrink-0"
                />
                <span>
                  Jeg har lest og godtar{" "}
                  <Link to="/vilkar" target="_blank" className="underline text-[#D97B66]">handelsvilkårene</Link>,
                  inkludert at jeg mister angreretten på digitale produkter.
                </span>
              </label>

              <button
                type="submit"
                data-testid="checkout-submit"
                disabled={loading}
                className="w-full bg-[#D97B66] hover:bg-[#C66A55] disabled:opacity-60 text-white rounded-full px-8 py-4 font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Lock className="w-4 h-4" strokeWidth={2} />
                {loading
                  ? lang === "en"
                    ? "Loading..."
                    : "Laster..."
                  : t("checkout_pay")}
              </button>

              <p className="text-xs text-[#6B726C] text-center">
                {t("checkout_secure")}
              </p>
            </form>
          </aside>
        </div>
      </div>
    </div>
  );
}
